import { fieldVisible } from '@eformulieren/engine'
import type { Visibility } from '@eformulieren/engine'
import { RESPONSE_SHAPE } from '@eformulieren/model'
import type { Control, Section, FieldId } from '@eformulieren/model'

/**
 * Nummering per sectie (R-04).
 *
 * Vragen zijn per sectie genummerd en niet doorlopend over het formulier, omdat
 * vragen wel of niet worden getoond en een doorlopende nummering dan niet klopt.
 * De renderer rekent de nummers over de zichtbare vragen; een veld dat door een
 * zichtbaarheidsconditie verborgen is telt niet mee (R-33).
 *
 * Nummering is presentatie (ontwerp 6.1): de stabiele veld-id's uit de definitie
 * veranderen er niet door, en het nummer staat nergens in de opslag.
 */

/**
 * Wat als vraag wordt genummerd.
 *
 * Een vraag is een veld dat iets van de invuller wil. Dat is niet hetzelfde als
 * een veld met een antwoord in de stand, dus deze afweging staat hier expliciet en
 * niet als hergebruik van `RESPONSE_SHAPE` alleen:
 *
 * - de weergavetypen (`status`, `notification`, `rating`, `progress`, `timer`,
 *   `subgrid`) vragen niets en tellen niet mee;
 * - een `verwijzing` vraagt niets: hij is alleen-lezen uit het dossier (R-56);
 * - een `criterium` vraagt wel iets, een oordeel met onderbouwing (R-147), ook al
 *   gaat de uitkomst naar de laag en niet naar de antwoorden;
 * - een vast `component` vraagt ook iets: hij levert zijn gegevens gestructureerd
 *   aan het dossier (R-53) langs zijn eigen weg.
 */
export function isQuestion(control: Control): boolean {
  if (control === 'verwijzing') return false
  if (control === 'criterium' || control === 'component') return true
  return RESPONSE_SHAPE[control] !== 'geen'
}

/**
 * Het nummer per zichtbare vraag in deze sectie, beginnend bij 1.
 *
 * Alleen de velden van de sectie zelf: een veld in een herhaalgroep is een kolom
 * in die groep en geen eigen vraag.
 */
export function numbersOf(section: Section, visibility: Visibility): Map<FieldId, number> {
  const numbers = new Map<FieldId, number>()
  let sectionNumber = 0
  for (const field of section.velden) {
    if (!isQuestion(field.control)) continue
    // Een akkoord is een verklaring met haar tekst naast het vinkje, zonder kop om een nummer voor
    // te zetten (besluit van 30 oktober 2026, R-89); ze telt daarom niet mee in de nummering.
    if ('akkoord' in field && field.akkoord === true) continue
    if (!fieldVisible(visibility, field.id)) continue
    sectionNumber += 1
    numbers.set(field.id, sectionNumber)
  }
  return numbers
}
